// @ts-nocheck
import * as R from 'ramda';
import { ReduxAsyncAction } from '../support/utils';
import debugLib from 'debug';
import dash_api from '../support/dash_api';
import NotificationActions from './NotificationActions';
import { getPartnerName } from '../components/PartnerManager/utils';
import type { AdPartner, AdPartnerBlueprint } from '../types/adPartnerTypes';
const debug = debugLib('AdPartnerActions');
export const mergeEntityWithBlueprint = (blueprint: AdPartnerBlueprint, entity: AdPartner | null | undefined): AdPartner => {
  let mergedConfig = {};

  /**
   * ad_partner_blueprint entries have a different postback type
   * (Map<String, Postback>) than ad_partner entries (Array<Postback>),
   * so make sure to return the AdPartner type.
   */
  const sanitizedBlueprint = { ...blueprint, postbacks: [] };
  mergedConfig = { ...mergedConfig, ...sanitizedBlueprint };

  if (entity) {
    const sanitizedEntity = { ...entity, postbacks: entity.postbacks || [] };
    mergedConfig = { ...mergedConfig, ...sanitizedEntity };
  } else {
    /**
     * Use plain blueprint (without ad network's entity) only if
     * entity's config doesn't exist.
     */
    mergedConfig.neverConfigured = true;
  }

  /**
   * Names of all events for all pre-defined postbacks are needed
   * because we use them to determine whether a URL is needed as input
   * while creating a postback.
   * If an event for which a postback is being created has a pre-defined
   * postback on the blueprint, then we wouldn't ask for a URL from the
   * user and use the pre-defined postback URL/template
   * If the event doesn't have a pre-defined postback on the blueprint, then
   * we try to fallback to custom_event on the blueprint. If custom_event
   * doesn't exist on the blueprint, then we need URL from the user
   */
  mergedConfig.eventsInBlueprintPostbacks = Object.keys(blueprint.postbacks || {});
  return mergedConfig;
};
export function mergeAppsWithBlueprints(
  appConfigs: Record<string, AdPartner>,
  blueprints: Array<AdPartnerBlueprint>
): Array<AdPartner> {
  const mergedConfigs: Array<AdPartner> = blueprints.map((blueprint: AdPartnerBlueprint): AdPartner => {
    // find appConfig based on machine_name
    const appConfig = appConfigs ? appConfigs[blueprint.machine_name] : null;
    return mergeEntityWithBlueprint(blueprint, appConfig);
  });
  // There may be ad networks for which there are no blueprints
  const nonBlueprintedConfigs: Array<AdPartner> = Object.keys(appConfigs).reduce((acc, machineName) => {
    if (
      !R.find(R.propEq('machine_name', machineName), blueprints) &&
      machineName.indexOf('a_custom_') !== -1 &&
      appConfigs &&
      appConfigs[machineName]
    ) {
      return [...acc, appConfigs[machineName]];
    }

    return acc;
  }, []);
  return mergedConfigs.concat(nonBlueprintedConfigs);
}
const GetAdPartners = new ReduxAsyncAction('AD_PARTNER_GET_PARTNER');
GetAdPartners.registerRequest(async ({ partner }) => {
  const [appConfigsPayload, blueprintsPayload] = await Promise.all([
    dash_api.get(`/v1/ad-networks/app`),
    dash_api.get(`/v1/ad-networks/global`),
  ]);
  const mergedConfigs = mergeAppsWithBlueprints(appConfigsPayload.data, blueprintsPayload.data);
  debug('App enabled partner list: ', appConfigsPayload.data);
  debug('Merged partners list: ', mergedConfigs);
  return {
    partners: mergedConfigs,
    selected: partner,
    hasPartnersLoaded: true,
  };
});
export const GET_PARTNERS = GetAdPartners.constants;
export const getAdPartners = GetAdPartners.request;
const GetAdPartnerForOrg = new ReduxAsyncAction('AD_PARTNER_GET_PARTNER_FOR_ORG');
GetAdPartnerForOrg.registerRequest((machine_name) => {
  let orgConfig;
  return dash_api
    .get(`/v1/ad-networks/org/${machine_name}`)
    .then(({ data }) => {
      orgConfig = data;
      return dash_api.get(`/v1/ad-networks/global-org/${machine_name}`);
    })
    .then(({ data }) => mergeEntityWithBlueprint(data, orgConfig));
});
export const GET_PARTNER_FOR_ORG = GetAdPartnerForOrg.constants;
export const getAdPartnerForOrg = GetAdPartnerForOrg.request;

/**
 * Fetch advertising_ontology
 * @return {Object} action
 */
const GetAdOntology = new ReduxAsyncAction('AD_PARTNER_GET_AD_ONTOLOGY');
GetAdOntology.registerRequest(() => {
  return dash_api.get(`/v1/ad-networks/ontology`).then(R.prop('data'));
});
export const GET_AD_ONTOLOGY = GetAdOntology.constants;
export const getAdOntology = GetAdOntology.request;

function trimPostbacks(partner) {
  const postbacks = partner.postbacks;
  const trimmedPostbacks = Array.isArray(postbacks)
    ? postbacks.map((p) => {
        p.template = p.template && p.template.trim();
        return p;
      })
    : postbacks;
  return { ...partner, postbacks: trimmedPostbacks };
}

const SaveAdPartner = new ReduxAsyncAction('AD_PARTNER_SAVE_ENABLE');
SaveAdPartner.registerRequest(
  ({
    partner,
    options = {
      withNotification: true,
    },
  }) => {
    partner = trimPostbacks(partner);
    return dash_api
      .put(`/v1/ad-networks/app`, {
        partner,
      })
      .then((payload) => {
        const { data } = payload;
        data.machine_name = partner.machine_name;
        data.neverConfigured = false;
        partner.enabled &&
          options &&
          options.withNotification &&
          NotificationActions.showNotification(`${getPartnerName(partner)} has been saved and enabled`, 'success');
        return data;
      });
  }
);
export const SAVE_ENABLE = SaveAdPartner.constants;
export const saveAdPartner = SaveAdPartner.request;
const DeleteInternalPartner = new ReduxAsyncAction('AD_PARTNER_DELETE_INTERNAL');
DeleteInternalPartner.registerRequest(({ partner }) => {
  const params = partner
    ? {
        params: {
          app_id: partner.app_id,
        },
      }
    : {};
  return dash_api.delete(`/v1/ad-networks/app/${partner.machine_name}`, params).then(() => {
    return dash_api.get('/v1/ad-networks/app').then((enabledPartners) => {
      return dash_api.get(`/v1/ad-networks/global/${partner.machine_name}`).then((partnerBlueprint) => {
        return {
          partnerBlueprint: partnerBlueprint.data,
          enabledPartners: enabledPartners.data,
          partner,
        };
      });
    });
  });
});
export const DELETE_INTERNAL_PARTNER = DeleteInternalPartner.constants;
export const deleteInternalPartner = DeleteInternalPartner.request;
const DisablePartner = new ReduxAsyncAction('AD_PARTNER_DISABLE');
DisablePartner.registerRequest(({ partner }) => {
  return dash_api
    .put(`/v1/ad-networks/app`, {
      partner: { ...partner, enabled: false },
    })
    .then((payload) => {
      const { data } = payload;
      data.machine_name = partner.machine_name;
      NotificationActions.showNotification(`${getPartnerName(partner)} has been disabled`, 'success');
      return data;
    });
});
export const DISABLE = DisablePartner.constants;
export const disablePartner = DisablePartner.request;
const SavePostback = new ReduxAsyncAction('AD_PARTNER_SAVE_POSTBACK');
SavePostback.registerRequest(({ postback }) => {
  return dash_api
    .post(`/v1/ad-networks/postback`, {
      postback,
    })
    .then((res) => {
      return {
        partner: res.data,
        event: postback.event,
      };
    });
});
export const SAVE_POSTBACK = SavePostback.constants;
export const savePostback = SavePostback.request;
const CreateInternalAdPartner = new ReduxAsyncAction('AD_PARTNER_CREATE_INTERNAL');
CreateInternalAdPartner.registerRequest(({ internalPartner }) => {
  return dash_api
    .post('/v1/ad-networks/internal-partner', {
      internalPartner,
    })
    .then((res) => {
      return {
        selectedPartner: res.data,
      };
    });
});
export const CREATE_INTERNAL_AD_PARTNER = CreateInternalAdPartner.constants;
export const createInternalAdPartner = CreateInternalAdPartner.request;
const ResetSettings = new ReduxAsyncAction('AD_PARTNER_RESET_SETTINGS');
ResetSettings.registerRequest((machine_name, partner) => {
  const params = partner
    ? {
        params: {
          app_id: partner.app_id,
        },
      }
    : {};
  return dash_api.delete(`/v1/ad-networks/app/${machine_name}`, params).then(() => {
    return dash_api.get('/v1/ad-networks/app').then((enabledPartners) => {
      return dash_api.get(`/v1/ad-networks/global/${machine_name}`).then((partnerBlueprint) => {
        return {
          partnerBlueprint: partnerBlueprint.data,
          enabledPartners: enabledPartners.data,
        };
      });
    });
  });
});
export const RESET_SETTINGS = ResetSettings.constants;
export const resetSettings = ResetSettings.request;
export const SELECT = 'AD_PARTNER_SELECT';
export function selectPartner({ partner }: { partner: Record<string, any> }) {
  debug(SELECT, partner);
  return {
    type: SELECT,
    data: partner,
  };
}
export const UPDATE = 'AD_PARTNER_UPDATE';
export function updateConfig({ config }: { config: Record<string, any> }) {
  debug(UPDATE, config);
  return {
    type: UPDATE,
    data: config,
  };
}
export const CHANGE_READY_FOR_SAVE = 'CHANGE_READY_FOR_SAVE';
export const changeReadyForSave = (isReady: boolean) => {
  debug(CHANGE_READY_FOR_SAVE, isReady);
  return {
    type: CHANGE_READY_FOR_SAVE,
    data: isReady,
  };
};
export const ROLLBACK_UNSAVED_CHANGES = 'ROLLBACK_UNSAVED_CHANGES';
export const rollbackUnsavedChanges = () => {
  debug(ROLLBACK_UNSAVED_CHANGES);
  return {
    type: ROLLBACK_UNSAVED_CHANGES,
  };
};

const GetCustomPartnersLimit = new ReduxAsyncAction('GET_CUSTOM_PARTNERS_LIMIT');
GetCustomPartnersLimit.registerRequest(async () => {
  const response = await dash_api.get('/v1/ad-network/custom-partners-limit');
  return response.data;
});
export const GET_CUSTOM_PARTNERS_LIMIT = GetCustomPartnersLimit.constants;
export const getCustomPartnersLimit = GetCustomPartnersLimit.request;

const LogAdPartnerSearch = new ReduxAsyncAction('LOG_AD_PARTNER_SEARCH');
LogAdPartnerSearch.registerRequest(async (text) => {
  const response = await dash_api.post('/v1/ad-network/log-search', { text });
  return response.data;
});
export const LOG_AD_PARTNER_SEARCH = LogAdPartnerSearch.constants;
export const logAdPartnerSearch = LogAdPartnerSearch.request;
